import axios from "axios";
import { isRequestCancelled } from "../classes/utils/RequestUtils";
import { getToken } from "../classes/utils/TokenUtils";

const API_URL = `${
  process.env.REACT_APP_BASE_URL || window.location.origin
}/api`;

const apiClient = axios.create({
  withCredentials: true,
  baseURL: API_URL,
});

apiClient.interceptors.request.use(
  config => {
    const token = getToken();

    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    if (config.data instanceof FormData) {
      config.headers["Content-Type"] = "multipart/form-data";
    } else {
      config.headers["Content-Type"] = "application/json";
    }

    return config;
  },
  error => Promise.reject(error)
);

apiClient.interceptors.response.use(
  response => {
    if (
      response.headers["content-type"] &&
      response.headers["content-type"].startsWith("image/")
    ) {
      response.data.contentType = response.headers["content-type"];
    }
    return response;
  },
  async error => {
    // Cancelled requests (AbortController) have no response — let them pass through
    if (isRequestCancelled(error)) {
      return Promise.reject(error);
    }

    // Handle other HTTP errors by routing to appropriate error pages
    const status = error.response?.status;

    if (status === 403) {
      // Forbidden - Navigate to access denied page
      window.location.href = "/access-denied";
      return Promise.reject(error);
    }

    // Note: 404 errors are now handled by individual components/services
    // This allows components to decide if missing resources are critical or optional
    // 409 conflicts are also handled locally so the UI can show contextual notifications.
    if (status === 409) {
      return Promise.reject(error);
    }

    if (status >= 500) {
      // CanDelete pre-check failures always contain "cannot be deleted" in
      // their detail message. Let callers handle these locally so they can
      // show a contextual notification instead of redirecting.
      const detail = error.response?.data?.detail;
      if (detail?.includes("cannot be deleted")) {
        return Promise.reject(error);
      }
      // Server errors (500, 502, 503, 504, etc.) - Navigate to error page
      const errorCode = status.toString();
      window.location.href = `/error?code=${errorCode}`;
      return Promise.reject(error);
    }

    // Network errors (no response)
    if (!error.response) {
      window.location.href = "/error?message=Network Error";
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export default apiClient;
